import {useEffect, useRef, useState} from "react";
import axios from "axios";

/**
 * Kleiner gemeinsamer Fetch-Hook für die Auswertungs-Endpunkte der
 * Verkaufsstatistik: lädt `url` mit `params` neu, sobald sich deren Inhalt
 * ändert (nicht nur die Objektreferenz - verglichen wird über JSON.stringify,
 * analog zum Signatur-Vergleich in DataGrid), bricht die vorherige Anfrage
 * beim Wechsel ab und meldet Ladezustand/Fehler einheitlich zurück.
 */
export default function useStatisticsData(url, params, {skip = false} = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(!skip);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const signature = JSON.stringify(params || {});

    useEffect(() => {
        if (skip || !url) {
            return undefined;
        }

        const controller = new AbortController();
        abortRef.current?.abort();
        abortRef.current = controller;
        setLoading(true);
        setError(null);
        // Alte Ergebnisse verwerfen, sobald ein neuer Abruf beginnt (z. B.
        // nach einem Filterwechsel) - sonst zeigen Komponenten mit einer
        // "loading && !data"-Weiche (KpiCards, RevenueTrendChart & Co.)
        // während des Nachladens weiterhin die zum VORHERIGEN Filter
        // gehörenden Werte an. Bei einem Zeitraum, dessen Abfrage mehrere
        // Sekunden dauert, wirkte das wie ein ignorierter Klick.
        setData(null);

        axios
            .get(url, {params, signal: controller.signal})
            .then(({data: response}) => {
                setData(response);
            })
            .catch((e) => {
                if (axios.isCancel(e)) {
                    return;
                }
                setError(e.response?.data?.message || e.message || "Die Daten konnten nicht geladen werden.");
            })
            .finally(() => {
                if (abortRef.current === controller) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, signature, skip]);

    return {data, loading, error};
}
